import { useMemo, useRef, useState } from "react";
import { CATALOG, CATEGORIES } from "@/features/space-designer/lib/catalog";
import { ItemShape } from "./ItemShape";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Search, Settings2, Plus, Upload, Trash2, Cloud, Pencil } from "lucide-react";
import type { Item } from "@/features/space-designer/lib/types";
import { usePlanner } from "@/features/space-designer/lib/store";
import { useSvgLibrary } from "@/features/space-designer/lib/svg-library";
import { fileToParsedSvg } from "@/features/space-designer/lib/svg-loader";
import { toast } from "sonner";
import { Link } from "wouter";
import { DrawComponent } from "./DrawComponent";

export function Library() {
  const [q, setQ] = useState("");
  const [sizes, setSizes] = useState<Record<string, { w: number; h: number }>>({});
  const addFromCatalog = usePlanner((s) => s.addFromCatalog);
  const addCustomSvg = usePlanner((s) => s.addCustomSvg);
  const customRoomsRaw = usePlanner((s) => s.scene.customRooms);
  const customRooms = customRoomsRaw ?? [];
  const addCustomRoom = usePlanner((s) => s.addCustomRoom);
  const removeCustomRoom = usePlanner((s) => s.removeCustomRoom);
  const hiddenCatalogIds = usePlanner((s) => s.scene.hiddenCatalogIds);
  const hideCatalogItem = usePlanner((s) => s.hideCatalogItem);
  const restoreHiddenCatalog = usePlanner((s) => s.restoreHiddenCatalog);
  const svgAssets = useSvgLibrary((s) => s.assets);
  const addAsset = useSvgLibrary((s) => s.addAsset);
  const removeAsset = useSvgLibrary((s) => s.removeAsset);
  const fileRef = useRef<HTMLInputElement>(null);
  const hiddenSet = new Set(hiddenCatalogIds ?? []);
  const filtered = CATALOG.filter(
    (c) => c.label.toLowerCase().includes(q.toLowerCase()) && !hiddenSet.has(c.id),
  );
  const filteredSvgs = svgAssets.filter((a) => a.name.toLowerCase().includes(q.toLowerCase()));
  const filteredCustomRooms = customRooms.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  async function onSvgUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    e.target.value = "";
    if (!files?.length) return;
    for (const f of Array.from(files)) {
      try {
        const parsed = await fileToParsedSvg(f);
        addAsset({
          name: parsed.name,
          inner: parsed.inner,
          viewBox: parsed.viewBox,
          width: parsed.width,
          height: parsed.height,
          palette: parsed.palette,
        });
      } catch (err) {
        toast.error(`${f.name}: ${err instanceof Error ? err.message : "could not parse"}`);
      }
    }
    toast.success("Added to your SVG library");
  }

  const presets = useMemo(
    () => (base: { w: number; h: number }) => [
      { label: "S", w: Math.round(base.w * 0.7), h: Math.round(base.h * 0.7) },
      { label: "M", w: base.w, h: base.h },
      { label: "L", w: Math.round(base.w * 1.4), h: Math.round(base.h * 1.4) },
    ],
    [],
  );

  return (
    <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col h-full">
      <div className="p-3 border-b border-border">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search components" className="pl-8 h-9" />
        </div>
        <div className="mt-2 flex gap-1.5">
          <input ref={fileRef} type="file" accept=".svg,image/svg+xml" multiple className="hidden" onChange={onSvgUpload} />
          <Button size="sm" variant="outline" className="flex-1 h-8 text-xs gap-1" onClick={() => fileRef.current?.click()}>
            <Upload className="w-3.5 h-3.5" /> SVG
          </Button>
          <Button asChild size="sm" variant="ghost" className="h-8 text-xs gap-1 px-2" title="Cloud SVG library">
            <Link href="/inventory/space/designer/svg-library">
              <Cloud className="w-3.5 h-3.5" />
            </Link>
          </Button>
        </div>
        <div className="mt-1.5">
          <DrawComponent
            trigger={
              <Button size="sm" variant="outline" className="w-full h-8 text-xs gap-1">
                <Pencil className="w-3.5 h-3.5" /> Draw component
              </Button>
            }
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-5">
        {filteredCustomRooms.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 px-1">My Rooms</div>
            <div className="grid grid-cols-2 gap-2">
              {filteredCustomRooms.map((r) => {
                const previewSize = 64;
                const scale = Math.min(previewSize / r.w, previewSize / r.h, 1);
                return (
                  <div
                    key={r.id}
                    className="group relative rounded-md border border-border bg-background hover:border-primary hover:shadow-sm transition"
                    title={`${r.name} · ${Math.round(r.w)}×${Math.round(r.h)} cm · ${r.children.length} parts`}
                  >
                    <div
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData("custom-room-id", r.id); }}
                      onDoubleClick={() => addCustomRoom(r.id, 400, 300)}
                      className="cursor-grab active:cursor-grabbing p-2 flex flex-col items-center gap-1"
                    >
                      <svg width={previewSize} height={previewSize} viewBox={`0 0 ${previewSize} ${previewSize}`}>
                        <g transform={`translate(${(previewSize - r.w * scale) / 2}, ${(previewSize - r.h * scale) / 2}) scale(${scale})`}>
                          <rect x={0} y={0} width={r.w} height={r.h} fill={r.color ?? "rgba(180,200,230,0.35)"} stroke="currentColor" strokeOpacity={0.4} strokeWidth={2 / scale} />
                          {r.children.map((ch, i) => {
                            const item: Item = {
                              id: `${r.id}-${i}`, type: ch.type, kind: ch.kind, shape: ch.shape,
                              label: ch.label, x: ch.x, y: ch.y, w: ch.w, h: ch.h,
                              rotation: ch.rotation ?? 0, color: ch.color, seats: ch.seats,
                            };
                            return <ItemShape key={i} item={item} />;
                          })}
                        </g>
                      </svg>
                      <div className="text-[10px] text-center leading-tight text-foreground/80 line-clamp-2">{r.name}</div>
                      <div className="text-[9px] text-muted-foreground">{Math.round(r.w)}×{Math.round(r.h)} cm</div>
                    </div>
                    <button
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition rounded p-1 bg-card/80 hover:bg-destructive/10 text-destructive border border-border"
                      title="Remove from library"
                      onClick={() => { removeCustomRoom(r.id); toast.success(`Removed “${r.name}”`); }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {filteredSvgs.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 px-1">My SVGs</div>
            <div className="grid grid-cols-2 gap-2">
              {filteredSvgs.map((a) => (
                <div
                  key={a.id}
                  className="group relative rounded-md border border-border bg-background hover:border-primary hover:shadow-sm transition"
                  title={a.name}
                >
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("custom-svg-id", a.id);
                    }}
                    onDoubleClick={() => addCustomSvg(400, 300, { name: a.name, inner: a.inner, viewBox: a.viewBox, width: a.width, height: a.height, palette: a.palette })}
                    className="cursor-grab active:cursor-grabbing p-2 flex flex-col items-center gap-1"
                  >
                    <svg width={64} height={64} viewBox={a.viewBox} preserveAspectRatio="xMidYMid meet" dangerouslySetInnerHTML={{ __html: a.inner }} />
                    <div className="text-[10px] text-center leading-tight text-foreground/80 line-clamp-2">{a.name}</div>
                  </div>
                  <button
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition rounded p-1 bg-card/80 hover:bg-destructive/10 text-destructive border border-border"
                    title="Remove from library"
                    onClick={() => removeAsset(a.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {CATEGORIES.map((cat) => {
          const items = filtered.filter((c) => c.kind === cat.kind);
          if (!items.length) return null;
          return (
            <div key={cat.kind}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 px-1">{cat.label}</div>
              <div className="grid grid-cols-2 gap-2">
                {items.map((c) => {
                  const previewSize = 64;
                  const size = sizes[c.id] ?? { w: c.w, h: c.h };
                  const scale = Math.min(previewSize / c.w, previewSize / c.h, 1);
                  const fakeItem: Item = {
                    id: c.id,
                    type: c.id,
                    kind: c.kind,
                    shape: c.shape,
                    label: c.label,
                    x: 0,
                    y: 0,
                    w: c.w,
                    h: c.h,
                    rotation: 0,
                    color: c.color,
                    seats: c.seats,
                  };
                  return (
                    <div
                      key={c.id}
                      className="group relative rounded-md border border-border bg-background hover:border-primary hover:shadow-sm transition"
                      title={`${c.label} · ${size.w}×${size.h} cm`}
                    >
                      <div
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("catalog", c.id);
                          e.dataTransfer.setData("catalog-dims", JSON.stringify(size));
                        }}
                        onDoubleClick={() => addFromCatalog(c.id, 400, 300, size)}
                        className="cursor-grab active:cursor-grabbing p-2 flex flex-col items-center gap-1"
                      >
                        <svg width={previewSize} height={previewSize} viewBox={`0 0 ${previewSize} ${previewSize}`}>
                          <g transform={`translate(${(previewSize - c.w * scale) / 2}, ${(previewSize - c.h * scale) / 2}) scale(${scale})`}>
                            <ItemShape item={fakeItem} />
                          </g>
                        </svg>
                        <div className="text-[10px] text-center leading-tight text-foreground/80 line-clamp-2">{c.label}</div>
                        <div className="text-[9px] text-muted-foreground">{size.w}×{size.h} cm</div>
                      </div>
                      <button
                        className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition rounded p-1 bg-card/80 hover:bg-destructive/10 text-destructive border border-border"
                        title="Remove from library"
                        onClick={() => { hideCatalogItem(c.id); toast.success(`Removed “${c.label}”`); }}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition rounded p-1 bg-card/80 hover:bg-accent border border-border"
                            title="Configure size"
                          >
                            <Settings2 className="w-3 h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="right" align="start" className="w-56 p-3 space-y-2">
                          <div className="text-xs font-semibold">{c.label}</div>
                          <div className="flex gap-1">
                            {presets({ w: c.w, h: c.h }).map((p) => (
                              <Button
                                key={p.label}
                                size="sm"
                                variant={size.w === p.w && size.h === p.h ? "default" : "outline"}
                                className="h-7 px-2 text-xs flex-1"
                                onClick={() => setSizes((s) => ({ ...s, [c.id]: { w: p.w, h: p.h } }))}
                              >
                                {p.label}
                              </Button>
                            ))}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Width (cm)</Label>
                              <Input
                                type="number"
                                value={size.w}
                                className="h-7 text-xs"
                                onChange={(e) => setSizes((s) => ({ ...s, [c.id]: { w: Math.max(10, Number(e.target.value) || 0), h: size.h } }))}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Height (cm)</Label>
                              <Input
                                type="number"
                                value={size.h}
                                className="h-7 text-xs"
                                onChange={(e) => setSizes((s) => ({ ...s, [c.id]: { w: size.w, h: Math.max(10, Number(e.target.value) || 0) } }))}
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="w-full h-7 text-xs gap-1"
                            onClick={() => addFromCatalog(c.id, 400, 300, size)}
                          >
                            <Plus className="w-3 h-3" /> Add to canvas
                          </Button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {(hiddenCatalogIds?.length ?? 0) > 0 && (
          <button
            onClick={restoreHiddenCatalog}
            className="w-full rounded border border-dashed border-border py-1.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted/40 transition"
          >
            Restore {hiddenCatalogIds!.length} hidden component{hiddenCatalogIds!.length === 1 ? "" : "s"}
          </button>
        )}
      </div>
      <div className="p-3 border-t border-border text-[11px] text-muted-foreground">Drag items onto the canvas.</div>
    </aside>
  );
}